import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { queryClient } from "@/lib/queryClient";
import { playMessageReceived, playNotification } from "@/lib/sounds";

type MessageHandler = (data: any) => void;

interface WebSocketContextType {
  ws: WebSocket | null;
  subscribe: (type: string, handler: MessageHandler) => () => void;
}

const WebSocketContext = createContext<WebSocketContextType>({
  ws: null,
  subscribe: () => () => {},
});

export function useWebSocket() {
  return useContext(WebSocketContext);
}

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [ws, setWs] = useState<WebSocket | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const handlersRef = useRef<Map<string, Set<MessageHandler>>>(new Map());

  const subscribe = useCallback((type: string, handler: MessageHandler): (() => void) => {
    if (!handlersRef.current.has(type)) {
      handlersRef.current.set(type, new Set());
    }
    handlersRef.current.get(type)!.add(handler);
    return () => { handlersRef.current.get(type)?.delete(handler); };
  }, []);

  const emit = useCallback((type: string, data: any) => {
    handlersRef.current.get(type)?.forEach(h => h(data));
  }, []);

  useEffect(() => {
    if (!user) {
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; setWs(null); }
      return;
    }

    let cancelled = false;

    function connect() {
      if (cancelled) return;
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const socket = new WebSocket(`${protocol}//${window.location.host}/ws`);

      socket.onopen = () => {
        socket.send(JSON.stringify({ type: "auth", userId: user!.id }));
        wsRef.current = socket;
        if (!cancelled) setWs(socket);
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Emit to component-level subscribers first
          emit(data.type, data);

          // Global: real-time notification delivery
          if (data.type === 'notification') {
            queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
            queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
            playNotification();
          }

          // Global: new message sound when NOT in the Messages page
          // (Messages page subscribes to 'new_message', so if subscribers exist it handles sound itself)
          if (data.type === 'new_message') {
            const hasPageHandler = (handlersRef.current.get('new_message')?.size ?? 0) > 0;
            if (!hasPageHandler) {
              playNotification();
              queryClient.invalidateQueries({ queryKey: ['/api/messages/unread-count'] });
            }
          }
        } catch {}
      };

      socket.onclose = () => {
        wsRef.current = null;
        if (!cancelled) { setWs(null); reconnectTimer.current = setTimeout(connect, 3000); }
      };

      socket.onerror = () => socket.close();
    }

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [user, emit]);

  return (
    <WebSocketContext.Provider value={{ ws, subscribe }}>
      {children}
    </WebSocketContext.Provider>
  );
}
